import Dialog from "../../General/ConfirmableDialog";
import { useState } from "react";
import {
  Box,
  CircularProgress,
  Typography,
  Button,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  IconButton,
  Tooltip,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { apiVariable } from "../../../api/apiVariable";
import { formatDate } from "../../../helpers/formatingFunctions";
import { handleDownloadFile } from "../../../api/handleDownloadFile";
import FileUploadModal from "../FileUploadModal/FileUploadModal";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Dayjs } from "dayjs";
import dayjs from "dayjs";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import useAuth from "../../../hooks/useAuth";
import StatusSnackbar from "../../General/StatusSnackbar/StatusSnackbar";
import { handleViewFile } from "../../../api/handleViewFile";

const fetchAssetFiles = async (
  token: string | null | undefined,
  folderId: string,
  assetType: string,
) => {
  const { data } = await axios.get(
    `${apiVariable}/get${assetType}Files/${folderId}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );
  return data;
};

interface DocumentsProps {
  folderId: string;
  token: string | null | undefined;
  assetType: "truck" | "trailer";
  unitNumber?: string;
  distinctCompanies: { id: string; label: string }[];
}

const Documents = ({
  folderId,
  token,
  assetType,
  unitNumber,
  distinctCompanies,
}: DocumentsProps) => {
  const [openCategoryDialog, setOpenCategoryDialog] = useState(false);
  const [assetFileType, setAssetFileType] = useState("");
  const [openUploadModal, setOpenUploadModal] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<{
    id: string;
    label: string;
  } | null>(null);
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);

  const [openReuploadCategoryDialog, setOpenReuploadCategoryDialog] =
    useState(false);
  const [reuploadAssetFileType, setReuploadAssetFileType] = useState("");
  const [openReuploadModal, setOpenReuploadModal] = useState(false);
  const [selectedReuploadCompany, setSelectedReuploadCompany] = useState<{
    id: string;
    label: string;
  } | null>(null);
  const [reuploadStartDate, setReuploadStartDate] = useState<Dayjs | null>(
    null,
  );
  const [reuploadEndDate, setReuploadEndDate] = useState<Dayjs | null>(null);
  const [selectedFileForReupload, setSelectedFileForReupload] =
    useState<any>(null);

  const [deleteStatus, setDeleteStatus] = useState<
    "idle" | "success" | "error"
  >("idle");
  const [deleteErrorMessage, setDeleteErrorMessage] = useState<string | null>(
    null,
  );
  const [fileToDelete, setFileToDelete] = useState<any>(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

  const [viewLoadingId, setViewLoadingId] = useState<string | number | null>(
    null,
  );
  const [downloadLoadingId, setDownloadLoadingId] = useState<
    string | number | null
  >(null);

  const { token: authToken } = useAuth();

  const {
    data: assetFiles,
    isLoading: isLoadingAssetFiles,
    isError: isErrorAssetFiles,
    error: assetFilesError,
    refetch: refetchAssetFiles,
  } = useQuery({
    queryKey: ["assetFiles", folderId],
    queryFn: () => fetchAssetFiles(token, folderId, assetType),
    enabled: !!folderId,
  });

  const handleOpenCategoryDialog = () => {
    setAssetFileType("");
    setOpenCategoryDialog(true);
  };

  const handleCategoryDialogClose = () => {
    setOpenCategoryDialog(false);
  };

  const handleCategorySubmit = () => {
    if (!assetFileType || !startDate || !endDate) return;
    setOpenCategoryDialog(false);
    setOpenUploadModal(true);
  };

  const handleOpenReuploadCategoryDialog = (file: any) => {
    setSelectedFileForReupload(file);
    setReuploadAssetFileType(file.type || "");
    setSelectedReuploadCompany(
      distinctCompanies.find(
        (company) => company.label === file.rentCompanyName,
      ) || null,
    );
    setReuploadStartDate(file.startDate ? dayjs(file.startDate) : null);
    setReuploadEndDate(file.expirationDate ? dayjs(file.expirationDate) : null);
    setOpenReuploadCategoryDialog(true);
  };

  const handleReuploadCategoryDialogClose = () => {
    setOpenReuploadCategoryDialog(false);
    setSelectedFileForReupload(null);
  };

  const handleReuploadCategorySubmit = () => {
    if (!reuploadAssetFileType || !reuploadStartDate || !reuploadEndDate)
      return;
    setOpenReuploadCategoryDialog(false);
    setOpenReuploadModal(true);
  };

  const handleDeleteClick = (file: any) => {
    setFileToDelete(file);
    setOpenDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    if (fileToDelete && fileToDelete.id) {
      try {
        await axios.delete(
          `${apiVariable}/deleteAssetFile/${fileToDelete.id}`,
          {
            headers: { Authorization: `Bearer ${authToken}` },
          },
        );
        setDeleteStatus("success");
        setOpenDeleteDialog(false);
        setFileToDelete(null);
        refetchAssetFiles();
      } catch (error: any) {
        setDeleteStatus("error");
        setDeleteErrorMessage(error.response?.data || "Failed to delete file");
      }
    }
  };

  const handleDeleteCancel = () => {
    setFileToDelete(null);
    setOpenDeleteDialog(false);
  };

  const handleDeleteSnackbarClose = () => setDeleteStatus("idle");

  const documentTitleOptions = [
    "Annual Inspection",
    "Certificate of Insurance",
    "Lease Agreement",
    "Registration",
    "Certificate of Liability Insurance",
    "Title",
  ];

  const documentsColumns = [
    { field: "type", headerName: "File Type", flex: 1 },
    {
      field: "startDate",
      headerName: "Start Date",
      flex: 1,
      valueFormatter: (params: any) => (params ? formatDate(params) : ""),
    },
    {
      field: "expirationDate",
      headerName: "Expiration Date",
      flex: 1,
      valueFormatter: (params: any) => (params ? formatDate(params) : ""),
    },
    { field: "rentCompanyName", headerName: "Company Name", flex: 1 },
    { field: "addedBy", headerName: "Added By", flex: 1 },
    {
      field: "view",
      headerName: "View",
      sortable: false,
      flex: 0.6,
      renderCell: (params: any) => {
        const { row } = params;
        const isLoading = viewLoadingId === row.id;
        return (
          <Button
            variant="contained"
            onClick={async () => {
              setViewLoadingId(row.id);
              try {
                await handleViewFile(row, "assetFolder", token, unitNumber);
              } finally {
                setViewLoadingId(null);
              }
            }}
            disabled={isLoading}
          >
            {isLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "View"
            )}
          </Button>
        );
      },
    },
    {
      field: "download",
      headerName: "Download",
      sortable: false,
      flex: 0.6,
      renderCell: (params: any) => {
        const { row } = params;
        const isLoading = downloadLoadingId === row.id;
        return (
          <Button
            variant="contained"
            onClick={async () => {
              setDownloadLoadingId(row.id);
              try {
                await handleDownloadFile(row, "assetFolder", token, unitNumber);
              } finally {
                setDownloadLoadingId(null);
              }
            }}
            disabled={isLoading}
          >
            {isLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Download"
            )}
          </Button>
        );
      },
    },
    {
      field: "reupload",
      headerName: "Reupload",
      sortable: false,
      flex: 0.6,
      renderCell: (params: any) => {
        const { row } = params;
        return (
          <Tooltip title="Reupload File">
            <IconButton
              color="primary"
              size="small"
              onClick={() => handleOpenReuploadCategoryDialog(row)}
              sx={{ ml: 1 }}
            >
              <CloudUploadIcon />
            </IconButton>
          </Tooltip>
        );
      },
    },
    {
      field: "delete",
      headerName: "Delete",
      sortable: false,
      flex: 0.6,
      renderCell: (params: any) => {
        const { row } = params;
        return (
          <Tooltip title="Delete File">
            <IconButton
              color="error"
              size="small"
              onClick={() => handleDeleteClick(row)}
              sx={{ ml: 1 }}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        );
      },
    },
  ];

  if (isLoadingAssetFiles) {
    return <CircularProgress />;
  }

  if (isErrorAssetFiles) {
    return (
      <Typography color="error">
        Error: {(assetFilesError as Error).message}
      </Typography>
    );
  }

  return (
    <>
      <Box sx={{ height: 400, width: { xs: "100%", sm: "80%" }, minWidth: 0 }}>
        <Button
          variant="contained"
          sx={{ mb: 2 }}
          onClick={handleOpenCategoryDialog}
        >
          Upload File
        </Button>
        <DataGrid
          rows={assetFiles || []}
          getRowId={(row) => row.id}
          columns={documentsColumns}
          key={folderId}
        />
      </Box>

      <Dialog open={openCategoryDialog} onClose={handleCategoryDialogClose}>
        <DialogTitle>Document meta-data</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Autocomplete
            freeSolo
            options={documentTitleOptions}
            inputValue={assetFileType}
            onInputChange={(_, value) => setAssetFileType(value)}
            renderInput={(params) => (
              <TextField {...params} label="Document title" fullWidth />
            )}
            sx={{ mt: 2 }}
          />

          <Autocomplete
            options={distinctCompanies}
            value={selectedCompany}
            onChange={(_, v) => setSelectedCompany(v)}
            renderInput={(params) => <TextField {...params} label="Company" />}
            sx={{ mt: 2 }}
          />

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Start date"
              value={startDate}
              onChange={(d: any) => setStartDate(d)}
              slotProps={{ textField: { fullWidth: true } }}
              sx={{ mt: 2 }}
            />
            <DatePicker
              label="End date"
              value={endDate}
              onChange={(d: any) => setEndDate(d)}
              minDate={startDate ?? undefined}
              slotProps={{ textField: { fullWidth: true } }}
              sx={{ mt: 2 }}
            />
          </LocalizationProvider>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCategoryDialogClose}>Cancel</Button>
          <Button
            onClick={handleCategorySubmit}
            disabled={
              !assetFileType ||
              !startDate ||
              !endDate ||
              endDate.isBefore(startDate)
            }
          >
            Next
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openReuploadCategoryDialog}
        onClose={handleReuploadCategoryDialogClose}
      >
        <DialogTitle>Reupload Document - Update Metadata</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Autocomplete
            freeSolo
            options={documentTitleOptions}
            inputValue={reuploadAssetFileType}
            onInputChange={(_, value) => setReuploadAssetFileType(value)}
            renderInput={(params) => (
              <TextField {...params} label="Document title" fullWidth />
            )}
            sx={{ mt: 2 }}
          />

          <Autocomplete
            options={distinctCompanies}
            value={selectedReuploadCompany}
            onChange={(_, v) => setSelectedReuploadCompany(v)}
            renderInput={(params) => <TextField {...params} label="Company" />}
            sx={{ mt: 2 }}
          />

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Start date"
              value={reuploadStartDate}
              onChange={(d: any) => setReuploadStartDate(d)}
              slotProps={{ textField: { fullWidth: true } }}
              sx={{ mt: 2 }}
            />
            <DatePicker
              label="End date"
              value={reuploadEndDate}
              onChange={(d: any) => setReuploadEndDate(d)}
              minDate={reuploadStartDate ?? undefined}
              slotProps={{ textField: { fullWidth: true } }}
              sx={{ mt: 2 }}
            />
          </LocalizationProvider>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleReuploadCategoryDialogClose}>Cancel</Button>
          <Button
            onClick={handleReuploadCategorySubmit}
            disabled={
              !reuploadAssetFileType ||
              !reuploadStartDate ||
              !reuploadEndDate ||
              reuploadEndDate.isBefore(reuploadStartDate)
            }
          >
            Next
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openDeleteDialog} onClose={handleDeleteCancel}>
        <DialogTitle>Delete Confirmation</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this file? This action cannot be
            undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDeleteConfirm}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {openUploadModal && folderId && (
        <FileUploadModal
          open={openUploadModal}
          onClose={() => {
            setOpenUploadModal(false);
            refetchAssetFiles();
          }}
          row={{ id: folderId }}
          type="assetFolder"
          unitNumber={unitNumber}
          assetFileType={assetFileType}
          companyId={selectedCompany?.id}
          startDate={startDate}
          endDate={endDate}
        />
      )}

      {openReuploadModal && folderId && (
        <FileUploadModal
          open={openReuploadModal}
          onClose={() => {
            setOpenReuploadModal(false);
            refetchAssetFiles();
          }}
          row={{ id: folderId }}
          type="assetFolder"
          unitNumber={unitNumber}
          assetFileType={reuploadAssetFileType}
          companyId={selectedReuploadCompany?.id}
          startDate={reuploadStartDate}
          endDate={reuploadEndDate}
          overwriteExisting={true}
        />
      )}

      <StatusSnackbar
        status={deleteStatus}
        successMessage="File deleted successfully"
        errorMessage={deleteErrorMessage}
        handleClose={handleDeleteSnackbarClose}
      />
    </>
  );
};

export default Documents;
